/**
 * Comment list + post mutation for the agent detail page.
 *
 * The agent endpoint returns ALL comments including internal notes and
 * API-key drafts; visibility is enforced server-side. The post
 * mutation handles both Public Reply (is_private=false) and Internal
 * Note (is_private=true). On success we invalidate the comments list,
 * the agent detail (status can transition WAITING_ON_CUSTOMER on
 * public reply), and the counts query.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  CommentCreateRequest,
  CommentResponse,
  CommentSendDraftRequest,
  CommentUpdateRequest,
} from "@/types/api";

import { agentTicketCountsQueryKey } from "./useAgentTicketCounts";
import {
  agentTicketAttachmentsQueryKey,
  agentTicketDetailQueryPrefix,
} from "./useAgentTicketDetail";
import { ticketEventsQueryKeyPrefix } from "./useTicketEvents";

/**
 * PREFIX key for one ticket's comments, window-agnostic. Mutations
 * invalidate this; TanStack matches keys by prefix, so both the windowed
 * and the full-history cache entries are invalidated by one call.
 */
export function agentTicketCommentsQueryKey(
  ticketNumber: number,
): readonly unknown[] {
  return ["agent", "tickets", "comments", ticketNumber] as const;
}

/** Exact key for one (ticket, window) pair. ``null`` = full history. */
export function agentTicketCommentsQueryKeyFor(
  ticketNumber: number,
  bodyWindow: number | null,
): readonly unknown[] {
  return [...agentTicketCommentsQueryKey(ticketNumber), bodyWindow] as const;
}

export interface UseAgentTicketCommentsOptions {
  enabled?: boolean;
  refetchIntervalMs?: number | false;
  /** Load the BODY of only the newest N comments (plus any unsent AI
   * draft); older rows arrive as metadata with ``body_omitted=true``.
   * ``null`` / omitted sends no param and gets the whole thread, which
   * is the endpoint's original behavior. See
   * plans/2026-08-24-ticket-detail-comment-windowing.md. */
  bodyWindow?: number | null;
}

export function useAgentTicketComments(
  ticketNumber: number,
  options: UseAgentTicketCommentsOptions = {},
): UseQueryResult<CommentResponse[]> {
  const bodyWindow = options.bodyWindow ?? null;
  return useQuery({
    queryKey: agentTicketCommentsQueryKeyFor(ticketNumber, bodyWindow),
    enabled: options.enabled !== false,
    refetchInterval: options.refetchIntervalMs ?? 15_000,
    // Expanding the history swaps the key. Without this the thread would
    // unmount to a spinner while the full payload (megabytes on a long
    // ticket - the exact thing this window exists to avoid) is in
    // flight. Holding the previous rows keeps the agent's place.
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await api.get<CommentResponse[]>(
        `/agent/tickets/${ticketNumber}/comments`,
        bodyWindow === null
          ? undefined
          : { params: { body_window: bodyWindow } },
      );
      return response.data;
    },
  });
}

export function useAddAgentComment(
  ticketNumber: number,
): UseMutationResult<CommentResponse, Error, CommentCreateRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CommentCreateRequest) => {
      const response = await api.post<CommentResponse>(
        `/agent/tickets/${ticketNumber}/comments`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentTicketCommentsQueryKey(ticketNumber),
      });
      void queryClient.invalidateQueries({
        queryKey: agentTicketDetailQueryPrefix,
      });
      void queryClient.invalidateQueries({
        queryKey: agentTicketCountsQueryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: ticketEventsQueryKeyPrefix(ticketNumber),
      });
    },
  });
}

/** Edit an existing internal note. Backend rejects public replies (409). */
export interface EditAgentCommentInput {
  commentId: string;
  body: CommentUpdateRequest;
}

export function useEditAgentComment(
  ticketNumber: number,
): UseMutationResult<CommentResponse, Error, EditAgentCommentInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ commentId, body }: EditAgentCommentInput) => {
      const response = await api.patch<CommentResponse>(
        `/agent/tickets/${ticketNumber}/comments/${commentId}`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentTicketCommentsQueryKey(ticketNumber),
      });
      void queryClient.invalidateQueries({
        queryKey: ticketEventsQueryKeyPrefix(ticketNumber),
      });
    },
  });
}

/**
 * Promote an API-key draft into a public reply and email it out.
 * Backend persists the agent's final edits, flips is_private and
 * sent_via_email, then calls the notification service. The mutation
 * invalidates the comments list, the ticket detail (status often
 * transitions OPEN/REOPENED -> WAITING_ON_CUSTOMER), the counts query,
 * and the audit timeline.
 */
export interface SendDraftCommentInput {
  commentId: string;
  body: CommentSendDraftRequest;
}

export function useSendDraftComment(
  ticketNumber: number,
): UseMutationResult<CommentResponse, Error, SendDraftCommentInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ commentId, body }: SendDraftCommentInput) => {
      const response = await api.post<CommentResponse>(
        `/agent/tickets/${ticketNumber}/comments/${commentId}/send`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentTicketCommentsQueryKey(ticketNumber),
      });
      void queryClient.invalidateQueries({
        queryKey: agentTicketDetailQueryPrefix,
      });
      void queryClient.invalidateQueries({
        queryKey: agentTicketCountsQueryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: ticketEventsQueryKeyPrefix(ticketNumber),
      });
    },
  });
}

/** Delete an internal note. Cascades to attached files. */
export function useDeleteAgentComment(
  ticketNumber: number,
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (commentId: string) => {
      await api.delete(
        `/agent/tickets/${ticketNumber}/comments/${commentId}`,
      );
    },
    onSuccess: () => {
      // Both comments and attachments shift: the comment row is gone
      // and the attachment cascade removes its files too. The audit
      // timeline ALSO shifts (a comment_deleted row was just emitted),
      // so invalidate every events cache for this ticket regardless
      // of the (limit, majorOnly) key suffix so both the default
      // major-only view and an open "Show All Events" view both
      // refresh on the next render rather than waiting for the
      // polling tick.
      void queryClient.invalidateQueries({
        queryKey: agentTicketCommentsQueryKey(ticketNumber),
      });
      void queryClient.invalidateQueries({
        queryKey: agentTicketAttachmentsQueryKey(ticketNumber),
      });
      void queryClient.invalidateQueries({
        queryKey: ticketEventsQueryKeyPrefix(ticketNumber),
      });
    },
  });
}
